import { connect } from 'react-redux';
import { useCallback, useState } from 'react';
import cn from 'classnames';

import { Row } from 'UIPackage/FormModule/components/Row';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { ErknmInspectionResultMessage } from 'KnmErknmPackage/MainModule/components/ErknmInspectionResultMessageMessage';
import { IInspectionResult } from 'APIPackage/CatalogsModule/interfaces/IInspectionResult';
import { InspectionResultActionTypeEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultActionTypeEnum';

import { IPermitDocs } from '../../../APIPackage/DictionariesModule/interfaces/IPermitDocs';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import styles from './KnmApproveDocs.module.scss';
import { KnmAttachmentsSigning } from '../../MainModule/components/KnmAttachmentsSigning';
import { IKnmFormRef } from '../../MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef } from '../../MainModule/selectors/knmSelectors';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { IKnmErknmFormDocTarget } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormDocTarget';
import { IDictionaryPermitDocSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryPermitDocSelectOption';
import { getDocTargetOptions, getDocTargetTypeOptions } from '../common/docTypeGetFunctions';
import { ErknmFieldMark } from '../../../KnmErknmPackage/MainModule/components/ErknmFieldMark';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';

interface IKnmApproveDocsProps {
  id: string;
  isWatchOnly: boolean;
  docOptions: IDictionarySelectOption<IPermitDocs>[] | undefined;
  fieldName: string;
  fieldValue: IKnmErknmFormDocTarget;
  selectedFields: (string | undefined)[];
  sIndex: number;
}

interface IKnmApproveDocsStoreProps {
  formRef: IKnmFormRef | undefined;
}

const KnmApproveDocsWithoutStore = ({
  id,
  isWatchOnly,
  docOptions,
  fieldName,
  fieldValue,
  selectedFields,
  sIndex,
  formRef,
}: IKnmApproveDocsStoreProps & IKnmApproveDocsProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const [isVisibleSignModal, setIsVisibleSignModal] = useState<number | null>(null);
  const remove = formRef?.current.mutators.remove;

  /**
   * Удаляет поля документа
   */
  const makeRemoveField = (index: number) => () => !isWatchOnly && remove && remove('docTarget', index);

  /**
   * Открытие модального окна загрузки документа
   */
  const handleOpenSignModal = useCallback((index: number) => setIsVisibleSignModal(index), []);

  /**
   * Закрытие модального окна загрузки документа
   */
  const handleCloseSignModal = useCallback(() => setIsVisibleSignModal(null), []);

  /**
   * Сбрасывает значение селекта выбора документа при смене типа отображаемых документов
   */
  const handleDocTargetTypeChange = (value: IDictionaryPermitDocSelectOption) => {
    const docTarget = formRef?.current.getState().values.docTarget;

    if (value.id !== fieldValue.docTargetType?.id && docTarget) {
      const docTargetNew = docTarget.map((doc, index) => {
        const docNew = { ...doc };

        if (index === sIndex) {
          docNew.ervkControlKindDocTarget = undefined;
        }

        return docNew;
      });

      formRef?.current.change('docTarget', docTargetNew);
    }
  };

  /**
   * Генерация имени гармошки объекта
   */
  const generateCollapseName = useCallback((inspectionResult: IInspectionResult | undefined) => {
    if (inspectionResult) {
      return <ErknmInspectionResultMessage inspectionResult={inspectionResult} />;
    }

    return undefined;
  }, []);

  const hasError = checkHasErrorInCollapseBlock(formRef, fieldName);

  return (
    <KnmCollapse
      id={id}
      theme="fields"
      title={`Документ №${sIndex + 1}`}
      onClose={makeRemoveField(sIndex)}
      openByDefault
      subtitle={generateCollapseName(fieldValue.inspectionResult)}
      headerClassName={cn([
        {
          [styles.BorderYellow]:
            fieldValue.inspectionResult &&
            fieldValue.inspectionResult.resultActionType === InspectionResultActionTypeEnum.LIGHT_YELLOW,
          [styles.BorderRed]:
            fieldValue.inspectionResult &&
            fieldValue.inspectionResult.resultActionType === InspectionResultActionTypeEnum.LIGHT_RED,
        },
      ])}
      error={isKnmCollapseOpened && hasError}
      changeOpenedCallback={setIsKnmCollapseOpened}
    >
      <>
        <Row margin="medium" isListItem>
          <SelectInputField
            className="shared-deleting-row"
            id={`${fieldName}.docTargetType`}
            name={`${fieldName}.docTargetType`}
            placeholder={
              <>
                Тип документа
                <ErknmFieldMark fieldName={`${fieldName}.docTargetType`} />
              </>
            }
            options={getDocTargetTypeOptions(docOptions)}
            isClearable={false}
            size="medium"
            disabled={isWatchOnly}
            validate={makeRequired('Поле не может быть пустым')}
            onChange={value => handleDocTargetTypeChange(value)}
          />
        </Row>
        {fieldValue.docTargetType && (
          <Row margin="medium" isListItem>
            <SelectInputField
              className="shared-deleting-row"
              id={`${fieldName}.ervkControlKindDocTarget`}
              name={`${fieldName}.ervkControlKindDocTarget`}
              placeholder={
                <>
                  Выберите документ
                  <ErknmFieldMark fieldName={`${fieldName}.ervkControlKindDocTarget`} />
                </>
              }
              options={getDocTargetOptions(selectedFields, fieldValue, docOptions)}
              isSearchable={true}
              isClearable={false}
              size="medium"
              disabled={isWatchOnly}
              validate={makeRequired('Поле не может быть пустым')}
            />
          </Row>
        )}
        {fieldValue.ervkControlKindDocTarget && (
          <>
            <Row margin="medium">
              <FieldBlock>
                <TextInputField
                  name={`${fieldName}.specificRequirementAndClause`}
                  placeholder="Конкретное проверяемое требование и пункт в разрешительном (ином) документе"
                  disabled={isWatchOnly}
                />
              </FieldBlock>
            </Row>
            <Row margin="medium">
              <FieldBlock id={`${fieldName}.file`}>
                <span>
                  {fieldValue.docTargetType?.value === 'PERMITTED' ? 'Разрешительный документ' : 'Иной документ'}
                </span>
                <Button
                  className={styles.AddFileButton}
                  text="Загрузить"
                  type="button"
                  variant="link"
                  color="primary"
                  onClick={() => handleOpenSignModal(sIndex)}
                  disabled={isWatchOnly || !!fieldValue?.file?.length}
                />
                <KnmAttachmentsSigning
                  key={`${fieldName}.file`}
                  name={`${fieldName}.file`}
                  isWatchOnly={isWatchOnly}
                  isVisibleSignModal={isVisibleSignModal === sIndex}
                  closeSignModal={handleCloseSignModal}
                  formRef={formRef}
                />
              </FieldBlock>
            </Row>
          </>
        )}
      </>
    </KnmCollapse>
  );
};

const mapStateToProps = (state: IRootState): IKnmApproveDocsStoreProps => ({
  formRef: getKnmFormRef(state),
});

export const KnmApproveDocs = connect(mapStateToProps)(KnmApproveDocsWithoutStore);
